import React, {useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  FileUpload as FileUploadIcon,
  GetApp as ExportIcon
} from '@mui/icons-material';
import {useSnackbar} from 'notistack';
import {
  useLeadImportServiceGetApiLeadContactsExcelTemplate,
  useLeadImportServiceGetApiLeadContactsExportAllLeads,
  useLeadImportServicePostApiLeadContactsImportExcelWithProgress
} from '../api/generated/queries/queries';
import {LeadImportReportDTO} from '../api/generated/requests/types.gen';
import ImportProgressTracker from '../components/LeadImport/ImportProgressTracker';
import {AuthService} from '../services/authService';

const ImportsPage: React.FC = () => {
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importReport, setImportReport] = useState<LeadImportReportDTO | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  // API hooks
  const { refetch: downloadTemplate } = useLeadImportServiceGetApiLeadContactsExcelTemplate();
  const { refetch: exportAllLeads } = useLeadImportServiceGetApiLeadContactsExportAllLeads();
  const importMutation = useLeadImportServicePostApiLeadContactsImportExcelWithProgress();

  const handleDownloadTemplate = async () => {
    try {
      // Pobierz bezpośrednio z API bez React Query transformacji
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = AuthService.getToken();
      
      const response = await fetch(`${API_URL}/api/lead-contacts/excel-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'szablon_importu_leadow.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      enqueueSnackbar('Szablon został pobrany', { variant: 'success' });
      
    } catch (error) {
      console.error('Template download error:', error);
      enqueueSnackbar('Błąd podczas pobierania szablonu', { variant: 'error' });
    }
  };

  const handleExportAllLeads = async () => {
    try {
      // Pobierz bezpośrednio z API bez React Query transformacji
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = AuthService.getToken();
      
      const response = await fetch(`${API_URL}/api/lead-contacts/export-all-leads`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      const currentDate = new Date().toISOString().split('T')[0];
      let filename = `eksport_leadow_${currentDate}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      enqueueSnackbar('Eksport został pobrany', { variant: 'success' });
      
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Błąd podczas eksportu kontaków', { variant: 'error' });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        enqueueSnackbar('Proszę wybrać plik Excel (.xlsx lub .xls)', { variant: 'error' });
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        enqueueSnackbar('Plik jest za duży. Maksymalny rozmiar to 10MB', { variant: 'error' });
        return;
      }

      setSelectedFile(file);
      setImportReport(null);
      // Start import immediately after file selection
      handleImport(file);
    }
  };

  const handleImport = async (file: File) => {
    setIsImporting(true);
    setImportDialogOpen(true); // Show progress dialog
    setImportReport(null);

    try {
      const response = await importMutation.mutateAsync({
        formData: { file: file }
      });

      if (response && typeof response === 'object' && 'importId' in response) {
        const responseData = response as { importId: string };
        setImportId(responseData.importId);
        enqueueSnackbar('Import rozpoczęty. Śledź postęp w oknie dialogowym.', { variant: 'info' });
      } else {
        enqueueSnackbar('Nieprawidłowa odpowiedź z serwera', { variant: 'error' });
        setIsImporting(false);
        setImportDialogOpen(false);
      }
    } catch (error) {
      enqueueSnackbar('Błąd podczas rozpoczynania importu', { variant: 'error' });
      setIsImporting(false);
      setImportDialogOpen(false);
    }
  };

  const handleDirectImport = () => {
    // Trigger file picker directly
    fileInputRef.current?.click();
  };

  const handleImportComplete = (report: LeadImportReportDTO) => {
    // Bezpieczne pobranie wartości z obiektu z domyślnymi wartościami
    const errorsCount = report.errorsCount ?? 0;
    const successfullyImported = report.successfullyImported ?? 0;
    const duplicatesRejected = report.duplicatesRejected ?? 0;
    const totalProcessed = report.totalProcessed ?? 0;

    // Sprawdź czy były jakiekolwiek błędy
    if (errorsCount > 0) {
      enqueueSnackbar(
        `Import zakończony z błędami! Przetworzono ${totalProcessed}, zaimportowano ${successfullyImported}, błędów: ${errorsCount}`,
        { variant: 'warning' }
      );
    }
    // Sprawdź czy wszystkie rekordy to duplikaty
    else if (successfullyImported === 0 && duplicatesRejected > 0) {
    enqueueSnackbar(
        `Import zakończony - wszystkie ${duplicatesRejected} rekordów to duplikaty. Nie dodano nowych kontaktów.`,
        { variant: 'info' }
    );
    }
    // Sprawdź czy nie zaimportowano niczego z innych powodów
    else if (successfullyImported === 0) {
      enqueueSnackbar(
        `Import zakończony - nie dodano żadnych nowych kontaktów z ${totalProcessed} przetworzonych rekordów.`,
        { variant: 'info' }
      );
    }
    // Sukces - zaimportowano przynajmniej jeden rekord
    else {
      let message = `Import zakończony pomyślnie! Zaimportowano ${successfullyImported} z ${totalProcessed} kontaktów.`;

      // Dodaj informacje o duplikatach jeśli były
      if (duplicatesRejected > 0) {
        message += ` Odrzucono ${duplicatesRejected} duplikatów.`;
      }

      enqueueSnackbar(message, { variant: 'success' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        {/* Import Excel Card */}
        <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: 280 }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <FileUploadIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                Import z pliku Excel
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
              Importuj kontakty z pliku Excel (.xlsx lub .xls). System automatycznie sprawdzi duplikaty i wygeneruje raport importu.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Maksymalny rozmiar pliku: 10MB
              </Typography>
            </Alert>
          </CardContent>

          <CardActions sx={{ justifyContent: 'center' }}>
            <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleDirectImport}
                sx={{
                  background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                  color: 'white',
                  borderRadius: '9999px', // pełna pastylka
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
                  },
                }}
            >
              Rozpocznij import
            </Button>
          </CardActions>
        </Card>

        {/* Template Download Card */}
        <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: 280 }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <DownloadIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                Szablon Excel
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
              Pobierz szablon Excel z prawidłową strukturą kolumn do importu kontaktów. Szablon zawiera przykładowe dane, które należy usunąć.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Szablon zawiera wszystkie wymagane pola
              </Typography>
            </Alert>
          </CardContent>

          <CardActions sx={{ justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{
                    borderRadius: '9999px',
                    fontSize: '0.8rem'
                  }}
            >
              Pobierz szablon
            </Button>
          </CardActions>
        </Card>

        {/* Export Leads Card */}
        <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: 280 }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <ExportIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                Eksport kontaków
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
              Eksportuj wszystkie swoje kontakty do pliku Excel. Plik będzie zawierał wszystkie zapisane dane kontaktowe dostępne w systemie.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Eksport zawiera wszystkie dane kontaków
              </Typography>
            </Alert>
          </CardContent>

          <CardActions sx={{ justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportAllLeads}
              sx={{
                borderRadius: '9999px',
                fontSize: '0.8rem'
              }}
            >
              Eksportuj leady
            </Button>
          </CardActions>
        </Card>
      </Box>

      {/* Instrukcje */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
          Instrukcje importu
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" component="div" sx={{ fontSize: '0.8rem' }}>
          <ol>
            <li><strong>Pobierz szablon Excel</strong> - Kliknij przycisk "Pobierz szablon" aby pobrać plik z prawidłową strukturą.</li>
            <li><strong>Wypełnij dane</strong> - Otwórz szablon w Excel i wypełnij danymi kontaktów. Usuń przykładowe wiersze.</li>
            <li><strong>Zapisz plik</strong> - Zapisz plik w formacie .xlsx lub .xls.</li>
            <li><strong>Rozpocznij import</strong> - Kliknij "Rozpocznij import" i wybierz przygotowany plik.</li>
            <li><strong>Śledź postęp</strong> - System pokaże postęp importu w czasie rzeczywistym.</li>
            <li><strong>Sprawdź raport</strong> - Po zakończeniu otrzymasz szczegółowy raport z wynikami importu.</li>
          </ol>
        </Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <strong>Uwaga:</strong> System automatycznie sprawdza duplikaty na podstawie numeru telefonu.
            Duplikaty zostaną odrzucone i uwzględnione w raporcie.
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <strong>Duplikaty:</strong> Jeśli wszystkie rekordy w pliku to duplikaty już istniejących kontaktów,
            import zostanie zakończony bez błędu, ale nie zostaną dodane nowe kontakty. To normalne zachowanie systemu.
          </Typography>
        </Alert>
      </Paper>

      {/* Hidden file input for direct file selection */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
      />

      {/* Import Progress Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={() => !isImporting && setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isImporting}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '0.8rem' }}>Import kontaktów z pliku Excel</Typography>
            <Box>
              {!isImporting && (
                <IconButton onClick={() => setImportDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedFile && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Importowany plik: <strong>{selectedFile.name}</strong>
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                Rozmiar: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Alert>
          )}

          {importId && (
            <ImportProgressTracker
              importId={importId}
              onComplete={(report) => {
                setImportReport(report);
                setIsImporting(false);
                setImportId(null);
                handleImportComplete(report);
              }}
              onError={() => {
                setIsImporting(false);
                setImportId(null);
              }}
            />
          )}

          {importReport && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                Raport importu
              </Typography>
              
              <Alert 
                severity={importReport.errorsCount === 0 ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  <strong>Przetworzono:</strong> {importReport.totalProcessed} kontaktów<br />
                  <strong>Zaimportowano:</strong> {importReport.successfullyImported} kontaktów<br />
                  <strong>Duplikaty odrzucone:</strong> {importReport.duplicatesRejected}<br />
                  <strong>Błędy:</strong> {importReport.errorsCount}
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImportsPage;